import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { circularProgressClasses } from '@mui/material/CircularProgress';
import iconButtonClasses from '@mui/material/IconButton/iconButtonClasses';
import { svgIconClasses } from '@mui/material/SvgIcon';
import type { Components, Theme } from '@mui/material/styles';

import {
  gradientSpinner,
  insetStroke
} from '@/design-system/button/buttonTheme';

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    variant?: 'standard' | 'filled';
  }

  interface IconButtonPropsColorOverrides {
    accent: true;
  }

  interface IconButtonPropsSizeOverrides {
    xsmall: true;
  }
}

const iconSize = (size: number) => ({
  [`& .${svgIconClasses.root}`]: { fontSize: size }
});

export const iconButtonTheme: Components<Theme>['MuiIconButton'] = {
  defaultProps: {
    variant: 'standard',
    color: 'secondary',
    size: 'medium',
    disableFocusRipple: true
  },
  styleOverrides: {
    root: ({ theme }) => {
      const { background, border, icon } = theme.vars.palette.semantic;
      const focusRing = `rgba(${theme.vars.palette.primary.mainChannel} / 0.2)`;

      return {
        boxSizing: 'border-box',
        padding: 0,
        borderRadius: 999,
        transition: theme.transitions.create([
          'background-color',
          'box-shadow'
        ]),
        '&:hover': {
          backgroundColor: background.secondary
        },
        [`&.${buttonBaseClasses.focusVisible}`]: {
          ...insetStroke(2, border.primary)
        },
        [`&.${iconButtonClasses.disabled}`]: {
          backgroundColor: 'transparent',
          color: icon.quaternary
        },
        [`& .${iconButtonClasses.loadingIndicator} .${circularProgressClasses.root}`]:
          gradientSpinner(theme),
        [`&.${iconButtonClasses.loading} > :not(.${iconButtonClasses.loadingWrapper})`]:
          {
            visibility: 'hidden'
          },
        variants: [
          {
            props: { size: 'large' },
            style: { width: 42, height: 42, ...iconSize(20) }
          },
          {
            props: { size: 'medium' },
            style: { width: 36, height: 36, ...iconSize(20) }
          },
          {
            props: { size: 'small' },
            style: { width: 32, height: 32, ...iconSize(20) }
          },
          {
            props: { size: 'xsmall' },
            style: { width: 24, height: 24, ...iconSize(16) }
          },
          {
            props: { variant: 'standard', color: 'primary' },
            style: { color: icon.secondary }
          },
          {
            props: { variant: 'standard', color: 'secondary' },
            style: { color: icon.tertiary }
          },
          {
            props: { variant: 'standard', color: 'secondary', size: 'xsmall' },
            style: { color: icon.quaternary }
          },
          {
            props: { variant: 'standard', color: 'accent' },
            style: { color: icon.accent }
          },
          {
            props: { variant: 'standard', color: 'error' },
            style: { color: icon.error }
          },
          {
            props: { variant: 'filled', size: 'small' },
            style: iconSize(16)
          },
          {
            props: { variant: 'filled' },
            style: {
              [`&.${iconButtonClasses.disabled}`]: {
                backgroundColor: background.disabled
              }
            }
          },
          {
            props: { variant: 'filled', color: 'primary' },
            style: {
              backgroundColor: background.primary,
              color: icon.inverse,
              '&:hover': {
                backgroundColor: background['primary-hover']
              },
              [`&.${buttonBaseClasses.focusVisible}`]: {
                outline: 'none',
                boxShadow: `0 0 0 4px ${focusRing}`
              },
              [`&.${iconButtonClasses.loading}`]: {
                backgroundColor: background.primary
              }
            }
          },
          {
            props: { variant: 'filled', color: 'secondary' },
            style: {
              backgroundColor: background.secondary,
              color: icon.tertiary,
              '&:hover': {
                backgroundColor: background['secondary-hover']
              },
              [`&.${iconButtonClasses.loading}`]: {
                backgroundColor: background.secondary
              }
            }
          }
        ]
      };
    }
  }
};
